import React from 'react';
import { X, ExternalLink } from 'lucide-react';
interface DesignPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function DesignPreviewModal({
  isOpen,
  onClose
}: DesignPreviewModalProps) {
  if (!isOpen) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-19" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-20">
        <div className="w-full max-w-4xl max-h-[90vh] bg-white border-2 border-gray-800 flex flex-col pointer-events-auto" data-id="element-21">
          {/* Header */}
          <div className="border-b-2 border-gray-200 p-6 bg-gray-50" data-id="element-22">
            <div className="flex items-start justify-between gap-4" data-id="element-23">
              <div data-id="element-24">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-25">
                  Design Preview — About This Demo
                </h2>
                <p className="text-sm text-gray-600 font-mono" data-id="element-26">
                  Interactive concept demonstration for Cook.AI
                </p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 transition-colors flex-shrink-0" data-id="element-27">
                <X className="w-6 h-6 text-gray-600" data-id="element-28" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-29">
            {/* Feedback CTA */}
            <div className="border-2 border-gray-900 bg-gray-900 text-white p-6 text-center space-y-3" data-id="element-30">
              <h3 className="text-xl font-bold" data-id="element-31">I Need Your Feedback!</h3>
              <p className="text-sm font-mono leading-relaxed max-w-2xl mx-auto" data-id="element-32">
                This is an early concept. Your input will directly shape the
                future of Cook.AI. Please take 1 minute to share your thoughts.
              </p>
              <a href="https://forms.gle/GsvFQoXsQqcCdJ3h7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-gray-900 hover:bg-gray-100 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-33">
                <span data-id="element-34">Leave Feedback</span>
                <ExternalLink className="w-4 h-4" data-id="element-35" />
              </a>
            </div>

            {/* What This Is */}
            <section className="space-y-3" data-id="element-36">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-37">
                What You're Looking At
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-38">
                This is an <strong data-id="element-39">interactive design preview</strong> — a
                clickable prototype that demonstrates how Cook.AI would work.
                Nothing here is real or functional. All actions show
                pre-configured results to illustrate the user experience.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-40">
                <strong data-id="element-41">No data is saved.</strong> Information you enter (like
                user profiles or preferences) won't persist or appear elsewhere
                in the demo. Each section operates independently to showcase
                specific features.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-42">
                The interface uses a <strong data-id="element-43">wireframe aesthetic</strong> —
                minimal styling focused on layout and functionality. The final
                application will have refined visual design while maintaining
                similar structure and interactions.
              </p>
            </section>

            {/* The Concept */}
            <section className="space-y-3" data-id="element-44">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-45">
                The Cook.AI Concept
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-46">
                Cook.AI is an AI-powered cooking assistant designed to make meal
                planning, recipe generation, and grocery shopping effortless.
                The platform learns your preferences, dietary restrictions, and
                cooking style to create personalized recipes that work for you
                and your guests.
              </p>
              <div className="bg-gray-50 border border-gray-200 p-4 space-y-2" data-id="element-47">
                <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-48">
                  Key Terminology
                </p>
                <ul className="text-sm text-gray-700 space-y-1" data-id="element-49">
                  <li data-id="element-50">
                    <strong data-id="element-51">Cook:</strong> The primary user generating recipes,
                    refining meals, and managing shopping lists
                  </li>
                  <li data-id="element-52">
                    <strong data-id="element-53">Guest:</strong> Anyone else eating the meal (family,
                    friends, roommates)
                  </li>
                  <li data-id="element-54">
                    <strong data-id="element-55">User:</strong> General term for both Cooks and
                    Guests
                  </li>
                </ul>
              </div>
            </section>

            {/* Core Features */}
            <section className="space-y-3" data-id="element-56">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-57">
                How It Works
              </h3>

              <div className="space-y-4" data-id="element-58">
                <div data-id="element-59">
                  <h4 className="font-bold text-sm text-gray-900 mb-2" data-id="element-60">
                    1. Smart Preferences
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-61">
                    Cooks and Guests provide dietary requirements and food
                    preferences in natural language — just like talking to a
                    friend. AI parses these into structured tags (e.g.,
                    "peanut-allergy", "vegetarian", "loves-spicy-food") that
                    automatically influence recipe generation.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-62">
                    Cooks manage all Guest profiles and can add preferences
                    directly or send SMS invitations for Guests to self-report.
                    The system remembers everyone's needs so you never have to
                    worry about dietary restrictions again.
                  </p>
                </div>

                <div data-id="element-63">
                  <h4 className="font-bold text-sm text-gray-900 mb-2" data-id="element-64">
                    2. Guided Recipe Generation
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-65">
                    Start by selecting who you're cooking for — the system
                    automatically loads all relevant preferences. Then describe
                    what you want in a prompt: cuisine type, ingredients to use
                    up, dietary goals, meal prep needs, or event details.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-66">
                    The AI presents several recipe concepts. Refine them through
                    conversation until you find the perfect match, then generate
                    the full recipe with ingredients, instructions, timing, and
                    techniques.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-67">
                    After generation, continue perfecting the recipe: adjust
                    details, enhance instructions, or ask questions. When ready,
                    publish it to your private library.
                  </p>
                </div>

                <div data-id="element-68">
                  <h4 className="font-bold text-sm text-gray-900 mb-2" data-id="element-69">
                    3. Recipe Library & Collaboration
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-70">
                    Browse your saved recipes with filters for creator, date,
                    ratings, and dietary needs. Favorite recipes stay at the top
                    for quick access.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-71">
                    Create revisions of any recipe to match your preferences. If
                    it's your recipe, choose to replace the original or save as
                    a new version. If it's someone else's, save your revision
                    separately — the original Cook can adopt your changes if
                    they like them.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-72">
                    Guests can rate and comment on recipes, helping Cooks refine
                    their work and highlighting crowd favorites.
                  </p>
                </div>

                <div data-id="element-73">
                  <h4 className="font-bold text-sm text-gray-900 mb-2" data-id="element-74">
                    4. Smart Shopping Lists
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-75">
                    Add recipes to your cart from the browser or recipe view.
                    All ingredients are automatically aggregated into a unified
                    shopping list. Adjust portions for each recipe and toggle
                    individual ingredients (skip items you already have).
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2" data-id="element-76">
                    Select your preferred store (Kroger, Meijer, Walmart) and
                    the list transforms into a store-specific version with real
                    inventory, pricing, and aisle locations. Print, download, or
                    text the list for in-person shopping, or place an online
                    order directly.
                  </p>
                </div>
              </div>
            </section>

            {/* Design Philosophy */}
            <section className="space-y-3" data-id="element-77">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-78">
                Design Philosophy
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-79">
                Cook.AI prioritizes <strong data-id="element-80">simplicity and speed</strong>. The
                interface is designed to be intuitive and feature-rich without
                overwhelming users. Authentication uses SMS only — no passwords,
                no email clutter. Communication is minimal and always via text
                message, the modern standard for digital interaction.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-81">
                The platform starts as a <strong data-id="element-82">private family tool</strong>,
                allowing close friends and family to share recipes, provide
                feedback, and build a collaborative cookbook. All recipes are
                private by default. If successful, it may expand to a broader
                audience while maintaining privacy controls.
              </p>
            </section>

            {/* Future Vision */}
            <section className="space-y-3" data-id="element-83">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-84">
                Future Vision
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-85">
                Cook.AI will remain <strong data-id="element-86">free with no ads</strong>. If
                monetization becomes necessary, the current idea is an optional
                in-cart tip (defaulting to 5% of cart cost) — there for generous
                users when and if they choose.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-87">
                The goal is to create a living, evolving cookbook that grows
                with your family and friends, making cooking less stressful and
                more enjoyable for everyone involved.
              </p>
            </section>

            {/* About the Creator */}
            <section className="space-y-3" data-id="element-88">
              <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2" data-id="element-89">
                About This Project
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-90">
                Cook.AI is being developed by{' '}
                <strong data-id="element-91">Stephen Hegele (Cole)</strong> as a personal project
                that got delightfully out of hand. What started as using ChatGPT
                for meal prep evolved into a full application concept.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-92">
                Before committing to full-scale development, Cole is validating
                the idea with real users. This design preview is part of that
                process — gathering feedback to ensure the concept is genuinely
                helpful before building the production application.
              </p>
              <div className="bg-gray-50 border border-gray-200 p-4 space-y-2" data-id="element-93">
                <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-94">
                  Contact & Inquiries
                </p>
                <p className="text-sm text-gray-700" data-id="element-95">
                  Email:{' '}
                  <a href="mailto:schegele@hotmail.com" className="text-gray-900 underline hover:no-underline" data-id="element-96">
                    schegele@hotmail.com
                  </a>
                </p>
                <p className="text-sm text-gray-700" data-id="element-97">
                  Demo URL:{' '}
                  <a href="https://cook.chegele.com/" target="_blank" rel="noopener noreferrer" className="text-gray-900 underline hover:no-underline" data-id="element-98">
                    cook.chegele.com
                  </a>
                </p>
                <p className="text-xs text-gray-500 mt-2" data-id="element-99">
                  Business inquiries welcome. This concept is protected under
                  applicable intellectual property laws.
                </p>
              </div>
            </section>

            {/* Final CTA */}
            <div className="border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center space-y-3" data-id="element-100">
              <p className="text-sm font-mono text-gray-700" data-id="element-101">
                Your feedback shapes the future of this project.
              </p>
              <a href="https://forms.gle/GsvFQoXsQqcCdJ3h7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 px-6 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-102">
                <span data-id="element-103">Share Your Thoughts</span>
                <ExternalLink className="w-4 h-4" data-id="element-104" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>;
}